import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  User, 
  Lock, 
  Eye, 
  EyeOff, 
  Home, 
  RefreshCw, 
  X 
} from 'lucide-react';
import { CACHE_VERSION } from '../hooks';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin);
const API = `${BACKEND_URL}/api`;

function LoginPage() {
  const navigate = useNavigate();
  const [loginCredentials, setLoginCredentials] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    setIsLoading(true);
    setLoginError("");
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username: loginCredentials.username,
        password: loginCredentials.password
      });
      
      if (response.data.success) {
        const user = response.data.user;
        const authData = { data: true, timestamp: Date.now() };
        const userData = { data: user, timestamp: Date.now() };
        localStorage.setItem(`${CACHE_VERSION}.church.authenticated`, JSON.stringify(authData));
        localStorage.setItem(`${CACHE_VERSION}.church.adminUser`, JSON.stringify(userData));
        if (response.data.token) {
          localStorage.setItem('adminToken', response.data.token);
        }
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginError(error.response?.data?.detail || "Invalid credentials. Please try again.");
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-purple-900 to-violet-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-violet-400/15 rounded-full blur-3xl translate-x-48 translate-y-48"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-4xl max-h-4xl opacity-10">
        <div className="w-full h-full bg-gradient-to-br from-amber-400 to-violet-400 rounded-full blur-3xl"></div>
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        {/* Logo Section */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", bounce: 0.5 }}
            className="inline-block"
          >
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-500 p-1 shadow-2xl shadow-amber-500/30">
              <div className="w-full h-full rounded-full overflow-hidden border-2 border-amber-300">
                <img src="/images/church-logo.png?v=2" alt="CCC VOB" className="w-full h-full object-cover" />
              </div>
            </div>
          </motion.div>
          <h1 className="text-2xl font-bold text-white mb-1">Admin Portal</h1>
          <p className="text-amber-200/80 text-sm">CCC VOB Parish · Church Management</p>
        </div>
        
        {/* Login Card */}
        <div className="bg-violet-950/60 backdrop-blur-xl rounded-3xl p-8 border border-amber-400/20 shadow-2xl shadow-violet-900/50">
          {loginError && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20"
            >
              <p className="text-red-300 text-sm flex items-center gap-2">
                <X className="w-4 h-4" />
                {loginError}
              </p>
            </motion.div>
          )}
          
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-amber-100 mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400/60" />
                <input
                  className="w-full bg-violet-900/50 border border-violet-700/60 rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-violet-400 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition-all"
                  placeholder="Enter username"
                  value={loginCredentials.username}
                  onChange={e => setLoginCredentials(prev => ({ ...prev, username: e.target.value }))}
                  onKeyPress={e => e.key === 'Enter' && handleLogin()}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-amber-100 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400/60" />
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full bg-violet-900/50 border border-violet-700/60 rounded-xl pl-12 pr-12 py-3.5 text-white placeholder-violet-400 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition-all"
                  placeholder="Enter password"
                  value={loginCredentials.password}
                  onChange={e => setLoginCredentials(prev => ({ ...prev, password: e.target.value }))}
                  onKeyPress={e => e.key === 'Enter' && handleLogin()}
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-400/70 hover:text-amber-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <motion.button
              onClick={handleLogin}
              disabled={isLoading}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all shadow-lg flex items-center justify-center gap-3 ${
                isLoading 
                  ? 'bg-violet-700 text-violet-300 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-violet-950 hover:from-amber-400 hover:to-yellow-300 shadow-amber-500/40'
              }`}
              whileHover={{ scale: isLoading ? 1 : 1.01 }}
              whileTap={{ scale: isLoading ? 1 : 0.99 }}
            >
              {isLoading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                  >
                    <RefreshCw className="w-5 h-5" />
                  </motion.div>
                  <span>Signing In...</span>
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </motion.button>
            
            <button
              onClick={() => navigate("/")}
              className="w-full py-3 rounded-xl bg-violet-800/50 hover:bg-violet-800 text-amber-100 font-medium transition-all flex items-center justify-center gap-2 border border-violet-700/50"
            >
              <Home className="w-4 h-4" />
              <span>Back to Homepage</span>
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <p className="text-center text-amber-200/50 text-xs mt-6">
          CCC VOB Parish · Secure Admin Access
        </p>
      </motion.div>
    </div>
  );
}

export default LoginPage;
